import React, { useCallback, useMemo } from 'react';
import {
  CareBenefitUnitOfMeasure,
  CareSubscriptionPaymentStatus,
  ManageMembershipRequestType,
  MutationType,
} from 'shared/types/graphql';
import { Icon, IconProps } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { titleCase } from 'shared/utils';
import format from 'date-fns/format';
import { TypographyVariant } from '@televet/kibble-ui/build/shared/types';
import { ActivityLogModel, CareActivityLogItem, CareEnrollmentPaymentDataType, LogItemWithData } from './types';
import {
  CareEnrollmentPaymentIcon,
  EnrollmentEventIcon,
  EnrollmentSupportEventProps,
  ReadableEnrollmentEventType,
  ReadableEnrollmentSupportEvent,
  TextColorByEnrollmentEventType,
} from './records/activityLogCard.records';
import { EnrollmentSupportEventCardBody } from './EnrollmentSupportEventCardBody';
import { BenefitUsageCardBody } from './BenefitUsageCardBody';
import { EnrollmentEventCardBody } from './EnrollmentEventCardBody';
import { CareEnrollmentPaymentBody } from './CareEnrollmentPayment';
import { Box, Divider, Flex } from '@televet/kibble-ui/build/chakra';
import { Menu } from '@televet/kibble-ui/build/components/Menu';

const getIconProps = (data: LogItemWithData): IconProps => {
  let iconProps: IconProps = {
    name: 'heartSolid',
    variant: 'dangerOnContrast',
  };

  switch (data.type) {
    case ActivityLogModel.CarePlanEnrollment:
      const { eventType } = data;
      iconProps = EnrollmentEventIcon[eventType];
      break;
    case ActivityLogModel.EnrollmentSupportEvent:
      const { manageMembershipRequestType } = data;
      iconProps = EnrollmentSupportEventProps[manageMembershipRequestType];
      break;
    case ActivityLogModel.CareEnrollmentPayment:
      const { status, subType } = data;
      iconProps =
        subType === CareEnrollmentPaymentDataType.Payment
          ? CareEnrollmentPaymentIcon[status as CareSubscriptionPaymentStatus]
          : CareEnrollmentPaymentIcon[subType];
      break;
    case ActivityLogModel.CareBenefitUsage:
    default:
      break;
  }

  return iconProps;
};

const getTitleProps = (data: LogItemWithData): { text: string; variant: TypographyVariant } => {
  let title = '';
  let variant: TypographyVariant = 'default';

  switch (data.type) {
    case ActivityLogModel.CarePlanEnrollment:
      const { eventType } = data;
      title = ReadableEnrollmentEventType[eventType];
      variant = TextColorByEnrollmentEventType[eventType];
      break;
    case ActivityLogModel.EnrollmentSupportEvent:
      const { manageMembershipRequestType } = data;
      title = ReadableEnrollmentSupportEvent[manageMembershipRequestType];
      variant = EnrollmentSupportEventProps[manageMembershipRequestType].variant || 'default';
      break;
    case ActivityLogModel.CareBenefitUsage:
      const { usageUnitOfMeasure, benefitTitle, quantity } = data;
      title = `Used ${usageUnitOfMeasure === CareBenefitUnitOfMeasure.Occurence ? `(${quantity})` : ''} ${titleCase(
        benefitTitle,
      )}`;
      break;
    case ActivityLogModel.CareEnrollmentPayment:
      if (data.subType === CareEnrollmentPaymentDataType.Dispute) {
        title = 'Payment Disputed';
        variant = 'danger';
      } else if (data.subType === CareEnrollmentPaymentDataType.Refund) {
        title = 'Refund Issued';
        variant = 'success';
      } else {
        title = data.status === CareSubscriptionPaymentStatus.Paid ? 'Payment Made' : 'Payment Failed';
        variant = data.status === CareSubscriptionPaymentStatus.Paid ? 'success' : 'danger';
      }
      break;
    default:
      break;
  }

  return { text: title, variant };
};

interface ActivityLogCardProps {
  item: CareActivityLogItem;
  handleDeleteNote: (id: string) => void;
}

const ActivityLogCard = ({ item, handleDeleteNote }: ActivityLogCardProps): JSX.Element => {
  const { data, user } = item;
  const eventIconProps = useMemo(() => getIconProps(data), [data]);
  const eventTitleProps = useMemo(() => getTitleProps(data), [data]);
  const eventDate = useMemo(() => new Date(item.date), [item.date]);
  const eventDateString = useMemo(() => format(eventDate, 'MM/dd/yyyy'), [eventDate]);
  const eventTimeString = useMemo(() => format(eventDate, 'h:mm aa'), [eventDate]);

  const handleMenuClick = useCallback(
    (value?: string) => {
      if (value === MutationType.Delete && data.type === ActivityLogModel.EnrollmentSupportEvent) {
        handleDeleteNote(data.id);
      }
    },
    [data, handleDeleteNote],
  );

  return (
    <Box width="100%" bgColor="background.default" borderRadius="lg" p={3} boxShadow="base" mt={3}>
      <Flex alignItems="center">
        <Icon {...eventIconProps} size="md" />
        <Text as="p" fontWeight="semibold" variant={eventTitleProps.variant} ml={2}>
          {eventTitleProps.text}
        </Text>
        {data.type === 'EnrollmentSupportEvent' &&
          data.manageMembershipRequestType === ManageMembershipRequestType.Note && (
            <Box ml="auto">
              <Menu
                buttonProps={{
                  leftIconName: 'dotsVertical',
                  showRightIcon: false,
                  variant: 'ghostNeutral',
                  size: 'sm',
                }}
                placement="bottom-end"
                listProps={{
                  onSelect: ({ value }): void => handleMenuClick(value),
                  menuItems: [{ label: 'Delete Note', value: MutationType.Delete }],
                }}
              />
            </Box>
          )}
      </Flex>
      <Box ml={7} mt={1}>
        <Flex alignItems="center" mb={1}>
          <Icon name="calendarDate" size="sm" variant="subtle" />
          <Flex ml={1} alignItems="center">
            <Text variant="subtle" size="sm">
              {eventDateString}
            </Text>
            <Divider orientation="vertical" mx={3} h={4} />
            <Text variant="subtle" size="sm">
              {eventTimeString}
            </Text>
          </Flex>
        </Flex>
        {data.type === ActivityLogModel.CarePlanEnrollment && <EnrollmentEventCardBody data={data} />}
        {data.type === ActivityLogModel.CareBenefitUsage && <BenefitUsageCardBody data={data} />}
        {data.type === ActivityLogModel.EnrollmentSupportEvent && <EnrollmentSupportEventCardBody data={data} />}
        {data.type === ActivityLogModel.CareEnrollmentPayment && <CareEnrollmentPaymentBody data={data} />}
        {user?.firstName && (
          <Flex alignItems="center">
            <Icon name="person" size="sm" variant="subtle" />
            <Text variant="subtle" size="sm" ml={1}>{`${!!user?.namePrefix ? user.namePrefix : ''} ${user.firstName} ${
              user.lastName && user.lastName
            } `}</Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default ActivityLogCard;
